/**
 * Month-grid aggregation for the trade calendar.
 *
 * Pure on purpose: bucketing trades into days, weeks and a month total is the
 * part that can be quietly wrong — a trade landing in the wrong week, or a
 * win-rate counting break-even trades as wins — and none of it needs a DOM to
 * verify.
 */

/** Local calendar day for a trade timestamp, as YYYY-MM-DD. */
export function dayKeyOf(value) {
  if (!value) return null;
  const raw = String(value);
  // A bare date is already a calendar day; parsing it as UTC then formatting
  // locally would shift it back a day for anyone west of Greenwich.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function monthKeyOf(value) {
  const day = dayKeyOf(value);
  return day ? day.slice(0, 7) : null;
}

/** Months that actually contain trades, newest first. */
export function monthsWithTrades(trades = []) {
  const keys = new Set();
  trades.forEach((t) => {
    const key = monthKeyOf(t.exitDate || t.entryDate || t.date);
    if (key) keys.add(key);
  });
  return Array.from(keys).sort().reverse();
}

function emptyDay() {
  return { pnl: 0, trades: 0, wins: 0, losses: 0 };
}

/**
 * Build the calendar grid for one month.
 *
 * Weeks start on Sunday and always contain seven cells, so the grid is
 * rectangular and the weekly totals line up with their rows. Days outside the
 * month are present but flagged, rather than omitted — a ragged first row
 * misaligns every column heading.
 *
 * @param {Array} trades
 * @param {string} monthKey  'YYYY-MM'
 */
export function buildMonthCalendar(trades = [], monthKey) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ''))) {
    return {
      monthKey: null, weeks: [], totals: { pnl: 0, trades: 0, tradingDays: 0 }, weekTotals: [],
    };
  }

  const [year, month] = monthKey.split('-').map(Number);
  const byDay = new Map();

  trades.forEach((t) => {
    const key = dayKeyOf(t.exitDate || t.entryDate || t.date);
    if (!key || key.slice(0, 7) !== monthKey) return;
    const entry = byDay.get(key) || emptyDay();
    const pnl = Number(t.profit ?? t.net_pnl ?? 0) || 0;
    entry.pnl += pnl;
    entry.trades += 1;
    // Break-even counts as neither. Folding it into wins inflates the rate on
    // exactly the days a trader is trying to read honestly.
    if (pnl > 0) entry.wins += 1;
    else if (pnl < 0) entry.losses += 1;
    byDay.set(key, entry);
  });

  const first = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const leading = first.getDay(); // 0 = Sunday

  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push({ inMonth: false, key: `pad-lead-${i}` });
  for (let d = 1; d <= daysInMonth; d += 1) {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const stats = byDay.get(key) || null;
    cells.push({
      inMonth: true,
      key,
      dayOfMonth: d,
      pnl: stats ? Math.round(stats.pnl * 100) / 100 : null,
      trades: stats ? stats.trades : 0,
      // Win rate over decided trades only, so a break-even day reads 0 of 0
      // rather than 0%.
      winRate: stats && (stats.wins + stats.losses) > 0
        ? (stats.wins / (stats.wins + stats.losses)) * 100
        : null,
    });
  }
  while (cells.length % 7 !== 0) cells.push({ inMonth: false, key: `pad-tail-${cells.length}` });

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const weekTotals = weeks.map((week, i) => {
    const days = week.filter((c) => c.inMonth && c.trades > 0);
    return {
      index: i + 1,
      pnl: Math.round(days.reduce((sum, c) => sum + (c.pnl || 0), 0) * 100) / 100,
      tradingDays: days.length,
    };
  });

  const active = cells.filter((c) => c.inMonth && c.trades > 0);
  return {
    monthKey,
    weeks,
    weekTotals,
    totals: {
      pnl: Math.round(active.reduce((sum, c) => sum + (c.pnl || 0), 0) * 100) / 100,
      trades: active.reduce((sum, c) => sum + c.trades, 0),
      tradingDays: active.length,
    },
  };
}

/** "May 2026" */
export function monthLabel(monthKey) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ''))) return '';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Step a month key by ±n, rolling the year over correctly. */
export function shiftMonth(monthKey, delta) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ''))) return monthKey;
  const [year, month] = monthKey.split('-').map(Number);
  const d = new Date(year, month - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Years that contain trades, newest first — the year picker's options. */
export function yearsWithTrades(trades = []) {
  const years = new Set();
  trades.forEach((t) => {
    const key = monthKeyOf(t.exitDate || t.entryDate || t.date);
    if (key) years.add(key.slice(0, 4));
  });
  return Array.from(years).sort().reverse();
}

/**
 * Move to `year`, keeping the current month where possible.
 *
 * Jumping to a year should land on the same month, not January — a trader
 * comparing "August last year" wants August. Falls back to the latest month
 * that year which actually traded, so the jump never lands somewhere blank
 * when data exists.
 */
export function jumpToYear(monthKey, year, monthsWithData = []) {
  const month = /^\d{4}-\d{2}$/.test(String(monthKey || '')) ? monthKey.slice(5, 7) : '01';
  const sameMonth = `${year}-${month}`;
  if (monthsWithData.includes(sameMonth)) return sameMonth;
  const inYear = monthsWithData.filter((m) => m.startsWith(`${year}-`));
  return inYear.length ? inYear[0] : sameMonth;
}
